/**
 * The web app moved from app.praesto.mademind.dev to app.praesto.mademind.it
 * (C-12, C-39), and the old name becomes a Firebase Hosting 301 to the new one.
 *
 * A copy of the app already opened on the old name does not see that 301. Its
 * service worker answers every navigation from the precached `index.html`, so
 * the old bundle keeps starting, and the service worker can never update
 * again: the browser refuses a service worker script behind a redirect. That
 * copy would run the old bundle for good, with its `/api` calls bouncing off
 * the redirect (a 301 turns a POST into a GET).
 *
 * So this bundle, released BEFORE the 301 is applied, recognises the old name,
 * does not start the app there, shows where it went (`MovedNotice`), and drops
 * the old name's service worker and caches. After the 301, a copy that picked
 * up this bundle either shows the notice from its cache or, with the service
 * worker gone, follows the 301 by itself. Neither runs the old bundle.
 */

/** Old host → origin it moved to. Only hosts that 301 to a new name belong here. */
export const MOVED_HOSTS: ReadonlyMap<string, string> = new Map([
  ["app.praesto.mademind.dev", "https://app.praesto.mademind.it"],
]);

type AddressParts = Pick<Location, "hostname" | "pathname" | "search" | "hash">;

/**
 * Where the current address lives now, with the same path, query and
 * fragment, so a link to a page lands on that page. Null when the host has not
 * moved.
 */
export function movedTarget(location: AddressParts): string | null {
  const origin = MOVED_HOSTS.get(location.hostname);
  if (!origin) return null;
  return `${origin}${location.pathname}${location.search}${location.hash}`;
}

export interface ForgetDeps {
  readonly serviceWorker: Pick<ServiceWorkerContainer, "getRegistrations"> | undefined;
  readonly caches: Pick<CacheStorage, "keys" | "delete"> | undefined;
}

/**
 * Unregisters every service worker of this origin and deletes its caches, so
 * the next visit to the old name goes to the network and meets the 301. It
 * includes the push worker: push enabled on the old name stops, and is enabled
 * again from the new one. Best-effort: never throws.
 */
export async function forgetOldHost(deps: ForgetDeps): Promise<void> {
  const { serviceWorker, caches } = deps;
  try {
    const registrations = (await serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));
  } catch {
    // Unsupported or blocked: there is nothing to unregister.
  }
  try {
    const keys = (await caches?.keys()) ?? [];
    await Promise.all(keys.map((key) => caches?.delete(key).catch(() => false)));
  } catch {
    // Same: no Cache Storage, nothing to delete.
  }
}

/** Live browser dependencies for `forgetOldHost`. */
export function browserForgetDeps(): ForgetDeps {
  return {
    serviceWorker: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
    caches: typeof caches === "undefined" ? undefined : caches,
  };
}
